import {cloneDeep, get} from 'lodash';
import {hideLoading, hideLoadingFunction, showLoading} from 'Core/helpers/loading';
import {getArray, getString, isset} from 'Core/helpers/data';
import {actionCreators} from "Core/store/reducers";
import * as listItemDataMap from "./dataMap/listItem";
import * as itemFilterDataMap from "./dataMap/filter";
import * as itemQuestionnairesListDataMap from "./dataMap/questionnairesList";
import {reducerStoreKey} from "./reducer";
import {SORT_ORDER} from "Core/const/global";
import {isSuccessful} from "Core/helpers/io";
import {isSystemAdmin} from '../../../../helpers/currentUser';
import {ioJsonAction, ioJsonDeleteItemAction, ioJsonFetchAction} from 'Core/store/actions/io';
import {EXTERNAL_QUESTIONNAIRE_ATTACH_STATUS} from './const';

/**
 * Fetch external questionnaires list
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {Object} [filter] - Fetch filter.
 * @param {number} [pageNo] - Ordinal of the page to load (pagination). Starts from 1.
 * @param {number} [perPage] - Number of items per page to load (pagination). Used system default if not specified.
 * @param {string} [sortBy='ordinal'] - Sort field name. The API defines sort fields.
 * @param {SortOrder} [sortDir='asc'] - Sort direction.
 * @return {function(*=): Promise<IoJsonFetchResponseObject>}
 */
export const fetchExternalQuestionnairesListAction = (
	abortCallback, filter = null, pageNo = 1, perPage, sortBy = 'ordinal', 
	sortDir = SORT_ORDER.ASC
) => dispatch => {
	const isSysAdmin = isSystemAdmin();
	
	return ioJsonFetchAction(
		abortCallback,
		'defaultAuthorizedApi',
		(isSysAdmin ?
			'admin/account-questionnaire-settings/search' :
			'member/account-questionnaire-settings/search'
		),
		'',
		filter,
		null,
		pageNo,
		perPage,
		sortBy,
		sortDir
	)(dispatch)
		// Get mapped data from response data
		.then(responseData => {
			if (isSuccessful(responseData)) {
				return ({
					...responseData,
					filter: itemFilterDataMap.input(get(responseData, 'filter')),
					data: getArray(responseData, 'data').map(i => listItemDataMap.input(i))
				});
			}
			return undefined;
		});
};

/**
 * Load external questionnaires list into Redux store
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {Object} [filter] - Fetch filter.
 * @param {number} [pageNo] - Ordinal of the page to load (pagination). Starts from 1.
 * @param {number} [perPage] - Number of items per page to load (pagination). Used system default if not specified.
 * @param {string} [sortBy] - Sort field name. The API defines sort fields.
 * @param {SortOrder} [sortDir] - Sort direction.
 * @param {boolean} [loadingOverlay=true] - Flag that specifies if a loading overlay is rendered. 
 * @return {function(*=): Promise<IoJsonFetchResponseObject>}
 */
export const loadExternalQuestionnairesListAction = (
	abortCallback, filter = null, pageNo = 1, perPage, sortBy, sortDir, loadingOverlay = true
) => dispatch => {
	const loading = (loadingOverlay === true ? showLoading("#main-page-table") : null);
	return fetchExternalQuestionnairesListAction(abortCallback, filter, pageNo, perPage, sortBy, sortDir)(dispatch)
		// Load data into Redux store
		.then(responseData => {
			if (responseData) dispatch(actionCreators[reducerStoreKey].setExternalQuestionnairesListData(responseData));
			if (loading !== null) hideLoading(loading);
			return responseData;
		});
};

/**
 * Clear external questionnaires list form Redux store
 *
 * @return {(function(*): void)|*}
 */
export const clearExternalQuestionnairesListAction = () => dispatch => {
	dispatch(actionCreators[reducerStoreKey].clearExternalQuestionnairesListData());
}

/**
 * Load external questionnaires item into Redux store
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {ExternalQuestionnairesListItemDataObject} item
 * @return {function(*=): Promise<ExternalQuestionnairesListItemDataObject>}
 */
export const loadExternalQuestionnairesItemAction = (abortCallback, item) => dispatch => {
	dispatch(actionCreators[reducerStoreKey].setExternalQuestionnairesItem(item));
	return Promise.resolve(item);
}

/**
 * Clear external questionnaires item from Redux store
 * @return {(function(*=): void)|*}
 */
export const clearExternalQuestionnairesItemAction = () => dispatch => {
	clearExternalQuestionnairesItemQuestionnairesListAction()(dispatch);
	dispatch(actionCreators[reducerStoreKey].clearExternalQuestionnairesItem());
};


// ---------------------------------------------------------------------------------------------------------------------


/**
 * Fetch external questionnaires list items questionnaires list
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} accountId - DB ID of the account.
 * @param {Object} [filter] - Fetch filter.
 * @param {number} [pageNo] - Ordinal of the page to load (pagination). Starts from 1.
 * @param {number} [perPage] - Number of items per page to load (pagination). Used system default if not specified.
 * @param {string} [sortBy] - Sort field name. The API defines sort fields.
 * @param {SortOrder} [sortDir='asc'] - Sort direction.
 * @return {function(*=): Promise<IoJsonFetchResponseObject>}
 */
export const fetchExternalQuestionnairesItemQuestionnairesListAction = (
	abortCallback, accountId, filter = null, pageNo = 1, perPage, sortBy, sortDir = SORT_ORDER.ASC
) => dispatch => {
	const isSysAdmin = isSystemAdmin();
	
	let filterToUse = (!filter ? {} : cloneDeep(filter));
	if (!getString(filter, 'attachedToStudy')) {
		filterToUse = {
			...filterToUse,
			attachedToStudy: (
				isSysAdmin ?
					EXTERNAL_QUESTIONNAIRE_ATTACH_STATUS.ALL :
					EXTERNAL_QUESTIONNAIRE_ATTACH_STATUS.UNATTACHED
			)
		};
	}
	
	return ioJsonFetchAction(
		abortCallback,
		'defaultAuthorizedApi',
		(isSysAdmin ?
			'admin/account-questionnaire-settings/fetch-external-questionnaires-by-account-id' : 
			'member/account-questionnaire-settings/fetch-external-questionnaires-by-account-id'
		),
		'',
		filterToUse,
		null,
		pageNo,
		perPage,
		sortBy,
		sortDir,
		{accountId}
	)(dispatch)
		// Get mapped data from response data
		.then(responseData => {
			if (isSuccessful(responseData)) {
				return ({
					...responseData,
					filter: itemFilterDataMap.input(get(responseData, 'filter')),
					data: getArray(responseData, 'data').map(i => itemQuestionnairesListDataMap.input(i))
				});
			}
			return undefined;
		});
};

/**
 * Load external questionnaires list items questionnaires list into Redux store
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} accountId - DB ID of the account.
 * @param {Object} [filter] - Fetch filter.
 * @param {number} [pageNo] - Ordinal of the page to load (pagination). Starts from 1.
 * @param {number} [perPage] - Number of items per page to load (pagination). Used system default if not specified.
 * @param {string} [sortBy] - Sort field name. The API defines sort fields.
 * @param {SortOrder} [sortDir] - Sort direction.
 * @param {boolean} [loadingOverlay=true] - Flag that specifies if a loading overlay is rendered.
 * @return {function(*=): Promise<IoJsonFetchResponseObject>}
 */
export const loadExternalQuestionnairesItemQuestionnairesListAction = (
	abortCallback, accountId, filter = null, pageNo = 1, perPage, sortBy, sortDir, 
	loadingOverlay = true
) => dispatch => {
	const loading = (loadingOverlay === true ? showLoading("#user-submitted-questionnaires-table") : null);
	return fetchExternalQuestionnairesItemQuestionnairesListAction(
		abortCallback, accountId, filter, pageNo, perPage, sortBy, sortDir
	)(dispatch)
		// Load data into Redux store
		.then(responseData => {
			if (responseData) dispatch(actionCreators[reducerStoreKey].setItemQuestionnairesListData(responseData));
			if (loading !== null) hideLoading(loading);
			return responseData;
		});
};

/**
 * Clear external questionnaires list items questionnaires list form Redux store
 *
 * @return {(function(*): void)|*}
 */
export const clearExternalQuestionnairesItemQuestionnairesListAction = () => dispatch => {
	dispatch(actionCreators[reducerStoreKey].clearItemQuestionnairesListData());
}

/**
 * Delete external questionnaires list items questionnaires list item
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {boolean} [isSystemAdmin=false] - Whether the user is a system admin.
 * @param {string} id - The ID of the external questionnaires list items questionnaires list item to delete.
 * @return {function(*=): Promise<IoJsonResponseObject>}
 */
export const deleteExternalQuestionnairesItemQuestionnairesListItemAction = (
	abortCallback, isSystemAdmin, id
) => dispatch => {
	const loading = showLoading('#delete-external-questionnaire-dialog .dialog-content-component > .buttons');
	return ioJsonDeleteItemAction(
		abortCallback,
		'defaultAuthorizedApi',
		(isSystemAdmin ?
			'admin/account-questionnaire-settings/delete-by-ids' : 
			'member/account-questionnaire-settings/delete-by-ids'
		),
		[id],
		undefined,
		hideLoadingFunction(loading),
	)(dispatch);
}

/**
 * Restore external questionnaires list items questionnaires list item
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} id - The ID of the external questionnaires list items questionnaires list item to restore.
 * @return {function(*=): Promise<IoJsonResponseObject>}
 */
export const restoreExternalQuestionnairesItemQuestionnairesListItemAction = (abortCallback, id) => dispatch => {
	const loading = showLoading('#restore-external-questionnaire-dialog .dialog-content-component > .buttons');
	return ioJsonDeleteItemAction(
		abortCallback,
		'defaultAuthorizedApi',
		'admin/account-questionnaire-settings/restore-by-ids',
		[id],
		undefined,
		hideLoadingFunction(loading),
	)(dispatch);
}

/**
 * Un-attach external questionnaires list items questionnaires list item
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} id - The ID of the external questionnaires list items questionnaires list item to un-attach.
 * @return {function(*=): Promise<IoJsonResponseObject>}
 */
export const unAttacheExternalQuestionnairesItemQuestionnairesListItemAction = (abortCallback, id) => dispatch => {
	const loading = showLoading('#un-attach-external-questionnaire-dialog .dialog-content-component > .buttons');
	return ioJsonDeleteItemAction(
		abortCallback,
		'defaultAuthorizedApi',
		'admin/account-questionnaire-settings/unattach-by-ids',
		[id],
		undefined,
		hideLoadingFunction(loading),
	)(dispatch);
}

/**
 * Fetch the lis of external questionnaire accounts available for the current user
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @return {function(*): *}
 */
export const fetchExternalQuestionnairesAccountsAction = abortCallback => dispatch => {
	return ioJsonAction(
		abortCallback,
		'defaultAuthorizedApi',
		'admin/account-questionnaire-settings/fetch-allowed-accounts',
	)(dispatch);
}

/**
 * Generate unique URL for the external questionnaire for the specified account
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {number} accountId - Account ID for which the unique URL should be generated.
 * @return {function(*): Promise<string|undefined>}
 */
export const generateExternalQuestionnaireUniqueUrlAction = (abortCallback, accountId) => dispatch => {
	const loading = showLoading(
		`#external-questionnaires-page #main-page-table [data-action-id="${accountId}"] ` +
		`[data-tooltipped]:has(.generate-external-questionnaire-url-btn)`
	);
	return ioJsonAction(
		abortCallback,
		'defaultAuthorizedApi',
		'admin/account-questionnaire-settings/get-external-questionnaire-url',
		{id: accountId},
		hideLoadingFunction(loading),
	)(dispatch)
		.then(res => (isset(res) ? getString(res, 'data') : undefined));
}